import { create } from 'zustand';
import type { components as AuthComponents } from '../../../../packages/types/generated/core-authorization.d.ts';
import { createLogoutSender, resetIdleSignOutClock } from '@aaimplatform/ui';
import { IDLE_SIGN_OUT_CHANNEL } from './idleSignOut';
import { platformAuthClient } from '../api/typed-clients';
import { apiErrorMessage } from '../api/error-message';
import { csrfHeaders } from '../lib/csrf';

// Empty-string fallback per F-VITE-AUTH-LOCALHOST-DEFAULT-FEDERATED-SPAS.
// Missing build-arg produces a relative-URL fetch that 404s visibly on the SPA's
// own origin rather than silently issuing CORS requests to a user's localhost.
// See knowledge-graph/projects/aaim/technology-innovation/incidents/2026-05-13-ops-console-auth-url-localhost-bake.md
const AUTH_SERVICE_URL = import.meta.env.VITE_AUTH_SERVICE_URL ?? '';

// browser-token-storage-hardening (.claude/rules/no-browser-token-storage.md):
// the platform-admin session now lives ONLY in the HttpOnly `auth-token` cookie
// set by platform-admin-api + aaim-core (WF-1). This store holds NO bearer or
// refresh token, persists NOTHING to localStorage, and rehydrates the user via
// GET /api/v1/auth/me on load. One-time purge of the legacy persisted blob that
// held accessToken/refreshToken before the cutover.
const LEGACY_PERSIST_KEY = 'aaim-platform-admin-storage';

/**
 * Sign-out requests are sent until the server hears one, so a request lost to
 * a dropped network cannot leave the session for a reload to restore
 * (DCF-62 review, 2026-11-02). The marker it keeps is a flag, no credential.
 */
const logoutSender = createLogoutSender('platform-admin:pending-logout');

type LogoutResponse = AuthComponents['schemas']['LogoutResponse'];

/**
 * POST /api/v1/auth/logout (aaim-core, through this SPA's /api/v1/auth
 * proxy). True only when aaim-core says the session ended (200,
 * LogoutResponse.success true); a 403 CSRF refusal, a 5xx or success false
 * leaves the sign-out pending.
 */
async function postLogout(): Promise<boolean> {
  // The auth-token cookie authenticates this POST, so it echoes the
  // csrf-token cookie as X-CSRF-Token (GH #4888; lib/csrf.ts).
  const res = await fetch('/api/v1/auth/logout', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...csrfHeaders() },
    body: JSON.stringify({}),
  });
  if (!res.ok) return false;
  const body = (await res.json().catch(() => null)) as LogoutResponse | null;
  return body?.success === true;
}
if (typeof window !== 'undefined') {
  try {
    window.localStorage.removeItem(LEGACY_PERSIST_KEY);
  } catch {
    /* ignore -- storage disabled / private mode */
  }
}

interface User {
  id: string;
  email: string;
  name: string;
  roles: string[];
  permissions?: string[];
  tenantId?: string;
  tenantName?: string;
}

// Raw user shape returned by GET /api/v1/auth/me (`{ user: { ... } }`) and the
// platform login response. The index signature preserves any extra runtime
// fields (impersonatedBy, originalTenantId, apexTenantId, etc.) on spread.
interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isInitialized: boolean;
  oauthLoading: boolean;
  oauthError: string | null;
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  loginWithProvider: (provider: string) => Promise<void>;
  completeOAuthExchange: (code: string) => Promise<void>;
  logout: () => void;
  initialize: () => Promise<void>;
}

// Authz literals are compared case-insensitively across the platform
// (.claude/rules/authz-case-insensitive.md). Roles are normalized to lowercase
// at the store boundary so pre-reload (login) and post-reload (/me) state agree.
function normalizeRoles(roles: readonly string[] | undefined | null): string[] {
  return (roles ?? []).map((r) => r.toLowerCase());
}

function toUser(raw: Record<string, unknown>): User {
  return { ...raw, roles: normalizeRoles(raw.roles as string[] | undefined) } as User;
}

// Hydrate the current user from the HttpOnly cookie via GET /api/v1/auth/me.
// credentials:'include' attaches the cookie; no token is read from storage.
// Returns the normalized user, or null when the session is absent/invalid.
async function fetchCurrentUser(): Promise<User | null> {
  try {
    const response = await fetch('/api/v1/auth/me', {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) return null;
    const data = (await response.json().catch(() => null)) as { user?: Record<string, unknown> } | null;
    if (!data?.user) return null;
    return toUser(data.user);
  } catch {
    return null;
  }
}

export const useAuthStore = create<AuthState>()((set) => ({
  user: null,
  isAuthenticated: false,
  isInitialized: false,
  oauthLoading: false,
  oauthError: null,

  // App load: rehydrate the user from the HttpOnly cookie. Gate the UI on
  // isInitialized so it does not flash logged-out before /me resolves.
  initialize: async () => {
    // A sign-out that never reached the server: send it again, and do not
    // restore the session it was meant to end.
    const endPendingSignOut = () => {
      logoutSender.send(postLogout);
      resetIdleSignOutClock(IDLE_SIGN_OUT_CHANNEL);
      set({ user: null, isAuthenticated: false, isInitialized: true });
    };
    if (logoutSender.pending()) {
      endPendingSignOut();
      return;
    }
    const user = await fetchCurrentUser();
    // Checked again: another tab may sign out while /me is in flight, and a
    // restore would clear its marker before that sign-out reaches the server.
    if (user && logoutSender.pending()) {
      endPendingSignOut();
      return;
    }
    if (user) {
      set({ user, isAuthenticated: true, isInitialized: true });
    } else {
      // No session: the next sign-in starts a full inactivity period.
      resetIdleSignOutClock(IDLE_SIGN_OUT_CHANNEL);
      set({ user: null, isAuthenticated: false, isInitialized: true });
    }
  },

  login: async (email: string, password: string) => {
    const { data, error, response } = await platformAuthClient.POST('/auth/login', {
      body: { email, password },
    });

    if (error || !data) {
      const status = response?.status ?? 0;
      const errorBody = error as { message?: string } | undefined;
      throw new Error(apiErrorMessage(errorBody) ?? (status === 401 ? 'Invalid credentials' : `Login failed (${status})`));
    }

    // browser-token-storage-hardening: the login response sets the HttpOnly
    // session cookie (platform-admin-api + aaim-core, WF-1). We DO NOT store the
    // body tokens -- the session lives only in the cookie. User kept in memory.
    set({
      user: toUser(data.user as Record<string, unknown>),
      isAuthenticated: true,
    });
  },

  // OAuth2 bootstrap -- exempt from openapi-fetch migration.
  // The authorization endpoint lives on a separate auth service and
  // the redirect flow does not return JSON that maps to a typed response.
  loginWithGoogle: async () => {
    set({ oauthLoading: true, oauthError: null });

    try {
      const response = await fetch(`${AUTH_SERVICE_URL}/api/v1/auth/oauth2/authorize`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          provider: 'google',
          callbackUrl: `${window.location.origin}/auth/callback`,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(
          errorData?.error?.message ?? `Authorization request failed (${response.status})`
        );
      }

      const data = await response.json() as { authorizationUrl: string; state: string };
      sessionStorage.setItem('oauth_state', data.state);
      window.location.href = data.authorizationUrl;
    } catch (error) {
      set({
        oauthLoading: false,
        oauthError: error instanceof Error ? error.message : 'OAuth login failed',
      });
    }
  },

  // Generic OAuth2/SSO provider flow (Google, Microsoft, SAML, etc.)
  loginWithProvider: async (provider: string) => {
    set({ oauthLoading: true, oauthError: null });

    try {
      const response = await fetch(`${AUTH_SERVICE_URL}/api/v1/auth/oauth2/authorize`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          provider,
          callbackUrl: `${window.location.origin}/auth/callback`,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(
          errorData?.error?.message ?? `Authorization request failed (${response.status})`
        );
      }

      const data = await response.json() as { authorizationUrl: string; state: string };
      sessionStorage.setItem('oauth_state', data.state);
      window.location.href = data.authorizationUrl;
    } catch (error) {
      set({
        oauthLoading: false,
        oauthError: error instanceof Error ? error.message : 'OAuth login failed',
      });
    }
  },

  // OAuth2/SSO callback: exchange the one-time ?code= for the HttpOnly session
  // cookie (POST /api/v1/auth/exchange returns NO tokens), then hydrate the user
  // via /me. The page JS never receives a bearer token.
  completeOAuthExchange: async (code: string) => {
    set({ oauthLoading: true, oauthError: null });

    const response = await fetch('/api/v1/auth/exchange', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    });

    if (!response.ok) {
      set({ oauthLoading: false });
      const body = (await response.json().catch(() => null)) as
        | { error?: { message?: string } }
        | null;
      throw new Error(body?.error?.message ?? `Sign-in exchange failed (${response.status})`);
    }

    const user = await fetchCurrentUser();
    if (!user) {
      set({ oauthLoading: false });
      throw new Error('Sign-in succeeded but the session could not be loaded.');
    }

    set({
      user,
      isAuthenticated: true,
      isInitialized: true,
      oauthLoading: false,
      oauthError: null,
    });
  },

  logout: () => {
    // Clear local session state immediately so the UI reflects logout without
    // waiting on the network. The session lives in the HttpOnly cookie, which
    // page JS cannot clear -- ask the backend to clear it, sending again until
    // it answers (logoutSender); initialize() restores no session whose
    // sign-out is still pending.
    set({ user: null, isAuthenticated: false, oauthLoading: false, oauthError: null });
    logoutSender.send(postLogout);
  },
}));

// A new sign-in clears a sign-out still pending from the previous session, so
// a late retry (it carries this browser's cookies) never ends the new one.
// initialize() never authenticates while one is pending, so any change to
// authenticated is a sign-in.
useAuthStore.subscribe((state, prev) => {
  if (state.isAuthenticated && !prev.isAuthenticated) logoutSender.clear();
});
